import { useCallback, type JSX } from 'react';
import { Menu, Text, Divider, Box } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconSettings, IconHelpCircle } from '@tabler/icons-react';
import { LogoutIcon } from '../../ui/Icon';
import { useGeoMarkupStore } from '@/store';
import { useMapOverlayMenu } from '@/hooks';
import { useWorkspaceSwitcher } from '@/hooks/useWorkspaceSwitcher';
import { selectCurrentWorkspace } from '@/store/selectors/authSelectors';
import { Z_INDEX_DROPDOWN } from '@/constants/zIndex';
import { buildMarketingUrl } from '@/constants/marketing';
import { UserAvatar } from '../UserAvatar';
import { AccountTypePill } from '../AccountTypePill';
import { WorkspaceSelector } from '@/components/layout/AppShell/TopBar';
import type { UserProfileMenuProps } from './UserProfileMenu.types';
import styles from './UserProfileMenu.module.css';

export function UserProfileMenu({
  size = 'md',
  className,
  hideWorkspaceSelector,
  hideSettings,
}: UserProfileMenuProps): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useMapOverlayMenu();
  const switchWorkspace = useWorkspaceSwitcher();

  const user = useGeoMarkupStore((state) => state.auth.user);
  const logout = useGeoMarkupStore((state) => state.auth.logout);
  const hasWorkspaces = useGeoMarkupStore(
    (state) => state.auth.workspaces.length > 0
  );
  const currentWorkspace = useGeoMarkupStore(selectCurrentWorkspace);

  const handleWorkspaceSwitch = useCallback(
    (workspaceId: string, shortId: string): void => {
      switchWorkspace(workspaceId, shortId);
      setIsMenuOpen(false);
    },
    [switchWorkspace, setIsMenuOpen]
  );

  const closeMenu = useCallback((): void => {
    setIsMenuOpen(false);
  }, [setIsMenuOpen]);

  if (!user) {
    return null;
  }

  const helpUrl = buildMarketingUrl(i18n.language, '/help');

  return (
    <Menu
      opened={isMenuOpen}
      onChange={setIsMenuOpen}
      shadow="md"
      width={320}
      position="bottom-end"
      offset={4}
      zIndex={Z_INDEX_DROPDOWN}
      transitionProps={{ duration: 150, transition: 'fade' }}
      classNames={{
        dropdown: styles.dropdownMenu,
        divider: styles.divider,
      }}
    >
      <Menu.Target>
        <button
          type="button"
          className={`${styles.trigger} ${className ?? ''}`}
          aria-label={t('user.profile.openMenu')}
          aria-expanded={isMenuOpen}
        >
          <div className={styles.avatarWrapper}>
            <UserAvatar
              displayName={user.displayName}
              avatarUrl={user.avatarUrl}
              size={size}
            />
            <AccountTypePill
              tier={user.subscriptionTier}
              className={styles.avatarBadge}
            />
          </div>
        </button>
      </Menu.Target>

      <Menu.Dropdown>
        <Box className={styles.userHeader}>
          <Text className={styles.userName}>{user.displayName}</Text>
          <Text className={styles.userEmail}>{user.email}</Text>
        </Box>

        {hasWorkspaces && !hideWorkspaceSelector && (
          <>
            <Box className={styles.workspaceSection}>
              <Menu.Label className={styles.workspaceLabel}>
                {t('user.profile.currentWorkspace')}
              </Menu.Label>
              <WorkspaceSelector
                onWorkspaceSwitch={handleWorkspaceSwitch}
                withinPortal={false}
                onAfterAction={closeMenu}
              />
            </Box>
            <Divider className={styles.divider} />
          </>
        )}

        {!hideSettings && (
          <>
            <Menu.Item
              onClick={() =>
                navigate(`/w/${currentWorkspace?.shortId ?? ''}/settings`)
              }
              leftSection={<IconSettings size={16} className={styles.icon} />}
              className={styles.menuItem}
            >
              {t('user.profile.settings')}
            </Menu.Item>

            <Divider className={styles.divider} />
          </>
        )}

        <Menu.Item
          component="a"
          href={helpUrl}
          target="_blank"
          rel="noopener noreferrer"
          leftSection={<IconHelpCircle size={16} className={styles.icon} />}
          className={styles.menuItem}
        >
          {t('user.profile.help')}
        </Menu.Item>

        <Divider className={styles.divider} />

        <Menu.Item
          onClick={logout}
          leftSection={<LogoutIcon size={16} className={styles.iconDanger} />}
          className={styles.menuItemDanger}
        >
          {t('user.profile.logout')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
